import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Mail, Building2, User, Calendar, Clock,
  CheckCircle, XCircle, AlertCircle, FileText, 
  ChevronDown, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import invitationService from '../services/invitationService';
import configService from '../services/configService';
import { PageLoader } from '../components/CommonLoader';

const InvitationDetails = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState(false);
  const [ndaText, setNdaText] = useState('');
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [loadingNda, setLoadingNda] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [showNdaModal, setShowNdaModal] = useState(false);

  useEffect(() => {
    if (token) {
      fetchInvitationDetails();
    } else {
      toast.error('Invalid invitation token');
      navigate('/my-invitations');
    }
  }, [token]);

  // Debug: Log when invitation changes
  useEffect(() => {
    if (invitation) {
    }
  }, [invitation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest('.actions-dropdown')) {
        setShowActionsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsDropdown]);

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      setLoadingNda(true);
      
      // Fetch invitation details
      const response = await invitationService.viewInvitation(token);
      
      // Handle the response - check all possible structures
      let invitationData = null;
      
      // The normalized response structure has data and isSingle properties
      if (response && response.isSingle === true && response.data) {
        invitationData = response.data;
      }
      // Check if response.data exists and is the invitation
      else if (response && response.data) {
        // If data is an object with invitation properties
        if (typeof response.data === 'object' && !Array.isArray(response.data)) {
          invitationData = response.data;
        }
      }
      // Direct response object
      else if (response && response.id && response.token) {
        invitationData = response;
      }
      
      // Final fallback
      if (!invitationData && response) {
        invitationData = response;
      }
      
      
      // Validate that we have actual invitation data
      if (!invitationData || typeof invitationData !== 'object') {
        throw new Error('Invalid invitation data');
      }
      
      // Set the invitation state
      setInvitation(invitationData);
      
      // Fetch NDA text if invitation is pending
      const actualInvData = invitationData.invitation || invitationData;
      
      
      if (actualInvData && (actualInvData.status === 0 || actualInvData.status_text?.toLowerCase() === 'pending')) {
        try {
          const ndaResponse = await configService.getPublicConfig('NDA_TEXT');
          
          
          // Extract the NDA text from the response
          let ndaContent = '';
          ndaContent = ndaResponse.NDA_TEXT;
      
          setNdaText(ndaContent);
        } catch (error) {
          setNdaText('');
        }
      }
      
      // Set loading to false and data ready after a small delay to ensure state is updated
      setTimeout(() => {
        setLoadingNda(false);
        setLoading(false);
        setDataReady(true);
      }, 100);
    } catch (error) {
      setLoading(false);
      setLoadingNda(false);
      setDataReady(false);
      toast.error('Failed to load invitation details');
      setTimeout(() => {
        navigate('/my-invitations');
      }, 1000);
    }
  };

  const handleAcceptClick = () => {
    setShowActionsDropdown(false);
    
    // NDA text is already fetched on page load, just show the modal
    setNdaAccepted(false);
    setShowNdaModal(true);
    
    // If no NDA text, allow direct acceptance
    if (!ndaText || ndaText.trim() === '') {
    }
  };
  
  const handleAcceptInvitation = async () => {
    try {
      setProcessingAction(true);
      setShowNdaModal(false);
      const response = await invitationService.accept(token);
      toast.success(response.message || 'Invitation accepted successfully');
      
      // Redirect to dataroom or invitations list
      const invData = invitation.invitation || invitation;
      if (response.dataroom_uuid || invData?.dataroom_uuid) {
        navigate(`/datarooms/${response.dataroom_uuid || invData.dataroom_uuid}/files`);
      } else {
        navigate('/my-invitations');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setProcessingAction(false);
      setNdaAccepted(false);
    }
  };

  const handleDecline = async () => {
    setShowActionsDropdown(false);
    
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return;
    }
    
    try {
      setProcessingAction(true);
      const response = await invitationService.decline(token);
      toast.success(response.message || 'Invitation declined');
      navigate('/my-invitations');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to decline invitation');
    } finally {
      setProcessingAction(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Show loader while loading or data not ready
  if (loading || !dataReady) {
    return <PageLoader />;
  }

  // After loading is complete, check if we have invitation data
  if (!invitation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invitation Not Found</h2>
          <p className="text-gray-600 mb-4">This invitation may have expired or been removed.</p>
          <button
            onClick={() => navigate('/my-invitations')}
            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Invitations
          </button>
        </div>
      </div>
    );
  }

  // Now we can safely access invitation properties - the data is in invitation.invitation
  const invitationData = invitation.invitation || invitation;
  const isPending = invitationData.status === 0 || invitationData.status_text?.toLowerCase() === 'pending';
  const isExpired = invitationData.expires_at && new Date(invitationData.expires_at) < new Date();

  // Get status color
  const getStatusColor = (status) => {
    const statusText = status?.toLowerCase() || '';
    if (statusText.includes('pending')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusText.includes('accepted') || statusText.includes('active')) {
      return 'bg-green-100 text-green-800';
    } else if (statusText.includes('declined') || statusText.includes('rejected')) {
      return 'bg-red-100 text-red-800';
    } else if (statusText.includes('expired')) {
      return 'bg-gray-100 text-gray-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Main render - at this point invitation is guaranteed to exist
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Navigation and Title */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/my-invitations')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Invitation Details</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Review your dataroom invitation
                  </p>
                </div>
              </div>
              
              
              {/* Actions Dropdown */}
              {isPending && !isExpired && (
                <div className="relative actions-dropdown">
                  <button
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    disabled={processingAction}
                    className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingAction ? 'Processing...' : 'Actions'}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </button>
                  
                  {showActionsDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        <button
                          onClick={handleAcceptClick}
                          className="flex items-center w-full text-sm text-gray-700 hover:bg-gray-100 px-3 py-1.5"
                        >
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                          Accept Invitation
                        </button>
                        <button
                          onClick={handleDecline}
                          className="flex items-center w-full text-sm text-gray-700 hover:bg-gray-100 px-3 py-1.5"
                        >
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                          Decline Invitation
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Status Bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-600">Status:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invitationData?.status_text)}`}>
                  {invitationData?.status_text || 'Unknown'}
                </span>
              </div>
              
              {isExpired && (
                <span className="text-sm font-medium text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  This invitation has expired
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Main Invitation Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Invitation Information
              </h2>
            </div>
            
            <div className="p-6">
              {/* Debug info - remove this after fixing */}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dataroom Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dataroom Name
                  </label>
                  <p className="text-sm text-gray-900 font-medium">
                    {invitationData?.dataroom?.name || 'Not specified'}
                  </p>
                </div>
                
                
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <p className="text-sm text-gray-900">
                    {(() => {
                      if (invitationData && invitationData.dataroom && invitationData.dataroom.company_name) {
                        return invitationData.dataroom.company_name;
                      }
                      return 'Not specified';
                    })()}
                  </p>
                </div>
                
                {/* Invited By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invited By
                  </label>
                  <div>
                    <p className="text-sm text-gray-900">
                      {(() => {
                        if (invitationData && invitationData.invited_by) {
                          const firstName = invitationData.invited_by.first_name || '';
                          const lastName = invitationData.invited_by.last_name || '';
                          const fullName = `${firstName} ${lastName}`.trim();
                          return fullName || invitationData.invited_by.email || 'Unknown';
                        }
                        return 'Unknown';
                      })()}
                    </p>
                    {invitationData && invitationData.invited_by && invitationData.invited_by.email && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {invitationData.invited_by.email}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Invitation Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invitation Status
                  </label>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(invitationData?.status_text)}`}>
                    {invitationData?.status_text || 'Unknown'}
                  </span>
                </div>
                
                {/* Expires At - Full Width */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires On
                  </label>
                  <p className={`text-sm ${isExpired ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                    {invitationData?.expires_at ? formatDate(invitationData.expires_at) : 'No expiry'}
                    {isExpired && ' (Expired)'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
      
      {/* NDA Modal */}
      {showNdaModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-600" />
                    Non-Disclosure Agreement
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Agreement required before accepting invitation</p>
                </div>
                <button
                  onClick={() => {
                    setShowNdaModal(false);
                    setNdaAccepted(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {ndaText && ndaText.trim() !== '' ? (
                <div>
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700 font-medium">
                      <AlertCircle className="w-4 h-4 inline mr-1 text-blue-600" />
                      Please read the Non-Disclosure Agreement below carefully
                    </p>
                  </div>
                  
                  <div className="border-2 border-gray-300 rounded-lg p-6 bg-white max-h-96 overflow-y-auto mb-6">
                    {ndaText.includes('<') ? (
                      <div 
                        className="prose prose-sm max-w-none text-gray-800"
                        dangerouslySetInnerHTML={{ __html: ndaText }}
                      />
                    ) : (
                      <div className="whitespace-pre-wrap text-gray-800 text-sm">
                        {ndaText}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
                    <input
                      type="checkbox"
                      id="nda-accept-modal"
                      checked={ndaAccepted}
                      onChange={(e) => setNdaAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                    />
                    <label htmlFor="nda-accept-modal" className="text-sm text-gray-700 cursor-pointer font-medium">
                      I agreed
                    </label>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowNdaModal(false);
                        setNdaAccepted(false);
                      }}
                      className="px-3 py-1.5 text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAcceptInvitation}
                      disabled={!ndaAccepted}
                      className="bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors px-3 py-1.5 text-sm"
                    >
                      Accept & Continue
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-600 mb-6">No NDA required for this invitation</p>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => setShowNdaModal(false)}
                      className="px-3 py-1.5 text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAcceptInvitation}
                      className="bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors px-3 py-1.5 text-sm"
                    >
                      Accept Invitation
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationDetails;